/**
 * BatchReportTab — INPUT / OUTPUT grid áp full skill `/table-style`.
 * (Spec §3 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`.)
 *
 * Mỗi section (INPUT / OUTPUT):
 *   - FilterBar global Search (quét toàn bộ TABLE_COLUMNS).
 *   - Per-column ▽ filter (text/checkboxes/date/number auto-detect).
 *   - CSS classes `app-dataframe` (compact) + `app-table-wrap` +
 *     `app-table-header-cell` + `app-table-cell-text` cho visual.
 *   - Sticky-left A:F (StackID/Date/Item/Bag/Mt) — Excel-style freeze pane.
 *   - Total row aggregation (SUM cho quantity, weighted-avg theo `mt` cho quality).
 *
 * Aggregation: SUM cho quantity/deducted/std qty, weighted-avg theo `mt`
 * cho quality + valuation. Cột `advanced` ẩn mặc định (toggle ngoài).
 */
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'

import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
} from '@/components/dataList'
import {
  INPUT_COLUMNS,
  OUTPUT_COLUMNS,
  type BatchCol,
  type BatchRow,
} from '../batchReportColumns'
import {
  formatAccessDate,
  fmtCurrency,
  fmtInt,
  fmtPercent,
  fmtPlain,
} from './formatters'
import { COLORS } from './styleTokens'

interface Props {
  inputRows: BatchRow[]
  outputRows: BatchRow[]
  showAdvanced: boolean
}

/* ---------- formatters → string for cell ---------------------------------- */

function formatCell(value: unknown, kind: BatchCol['kind']): string {
  if (value == null || value === '') return ''
  switch (kind) {
    case 'date': return formatAccessDate(value)
    case 'int': return fmtInt(value)
    case 'number': return fmtPlain(value, 3)
    case 'percent': return fmtPercent(value, 2)
    case 'currency': return fmtCurrency(value)
    default: return String(value)
  }
}

function isNumericKind(kind: BatchCol['kind']): boolean {
  return kind === 'int' || kind === 'number' || kind === 'percent' || kind === 'currency'
}

/* ---------- aggregation -------------------------------------------------- */

function toNum(v: unknown): number {
  if (v == null || v === '') return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function aggregateTotals(rows: BatchRow[], columns: BatchCol[]): BatchRow {
  const total: BatchRow = {}
  const mtTotal = rows.reduce((acc, r) => acc + toNum(r.mt), 0)
  for (const col of columns) {
    if (col.totalAgg === 'sum') {
      const sum = rows.reduce((acc, r) => acc + toNum(r[col.key]), 0)
      total[col.key] = sum
    } else if (col.totalAgg === 'wavg' && mtTotal > 0) {
      const wsum = rows.reduce((acc, r) => acc + toNum(r[col.key]) * toNum(r.mt), 0)
      total[col.key] = wsum / mtTotal
    } else {
      total[col.key] = ''
    }
  }
  total._isTotal = true
  return total
}

/* ---------- sticky-left offsets ----------------------------------------- */

function stickyOffsetMap(columns: BatchCol[]): Record<string, number> {
  const offset: Record<string, number> = {}
  let acc = 0
  for (const col of columns) {
    if (col.sticky) {
      offset[col.key] = acc
      acc += col.width
    }
  }
  return offset
}

/* ---------- inferred filter kind cho mỗi cột ---------------------------- */

/** Bản đồ BatchCol → ColumnLike cho skill inferColumnFilterKind. */
function asFilterColumn(col: BatchCol) {
  // Skill chỉ nhận 'date'/'datetime'/'number'/'percent'. Currency/int → number;
  // text giữ nguyên cho skill auto-detect text vs checkboxes theo distinct.
  const skillKind =
    col.kind === 'date' ? 'date'
      : col.kind === 'percent' ? 'percent'
        : col.kind === 'currency' || col.kind === 'int' || col.kind === 'number' ? 'number'
          : 'text'
  return { key: col.key, label: col.label, kind: skillKind, filter: col.filter }
}

/* ---------- section component -------------------------------------------- */

interface SectionProps {
  title: 'INPUT' | 'OUTPUT'
  columns: BatchCol[]
  rows: BatchRow[]
  showAdvanced: boolean
  storageKeyPrefix: string
}

function BatchSection({ title, columns, rows: baseRows, showAdvanced }: SectionProps) {
  const visibleColumns = useMemo(
    () => columns.filter((c) => showAdvanced || !c.advanced),
    [columns, showAdvanced],
  )
  const stickyOffsets = useMemo(() => stickyOffsetMap(visibleColumns), [visibleColumns])

  // --- Filter state (mỗi section có state riêng) -----------------------------
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length

  const clearAll = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    searchPopover.close()
  }

  // Search applies tất cả cột (kể cả advanced ẩn UI) — đảm bảo user gõ "BL-"
  // vẫn match StackID dù cột helper-sub đang ẩn.
  const searchableCols = columns
  const filteredRows = useMemo(
    () => baseRows.filter((r) =>
      rowMatchesSearch(r, searchTerm, searchableCols)
      && rowMatchesAllColumnFilters(r, columnFilters.filters),
    ),
    [baseRows, searchTerm, columnFilters.filters, searchableCols],
  )

  const total = useMemo(() => aggregateTotals(filteredRows, visibleColumns), [filteredRows, visibleColumns])

  // Popover meta (computed khi mở col popover)
  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(asFilterColumn(popoverColumn), baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, colPopover.key) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  return (
    <Stack spacing={0.75}>
      {/* Section heading line — title + row count chip. */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: COLORS.text, letterSpacing: 0.2 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={`${filteredRows.length.toLocaleString('en-US')}${filteredRows.length !== baseRows.length ? ` / ${baseRows.length}` : ''} rows`}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: COLORS.bgHeader,
            color: COLORS.textMuted,
            border: 'none',
          }}
        />
        <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted }}>
          {visibleColumns.length} of {columns.length} columns
        </Typography>
      </Stack>

      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAll}
        activeFilterCount={activeFilterCount}
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => searchPopover.open('search', e)}
        />
      </FilterBar>

      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap" sx={{ position: 'relative' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col) => {
                  const numeric = isNumericKind(col.kind)
                  const stickyLeft = col.sticky ? stickyOffsets[col.key] : undefined
                  return (
                    <TableCell
                      key={col.key}
                      align={numeric ? 'right' : 'left'}
                      className="app-table-header-cell"
                      style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                      sx={{
                        whiteSpace: 'nowrap',
                        position: stickyLeft != null ? 'sticky' : 'sticky',
                        ...(stickyLeft != null && { left: stickyLeft, zIndex: 6 }),
                      }}
                      title={col.label}
                    >
                      {col.label}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(col.key)}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: COLORS.textSubtle }}>
                      No rows match current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {visibleColumns.map((col) => {
                      const text = formatCell(row[col.key], col.kind)
                      const numeric = isNumericKind(col.kind)
                      const stickyLeft = col.sticky ? stickyOffsets[col.key] : undefined
                      return (
                        <TableCell
                          key={col.key}
                          align={numeric ? 'right' : 'left'}
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                          sx={{
                            ...(stickyLeft != null && {
                              position: 'sticky',
                              left: stickyLeft,
                              zIndex: 2,
                              bgcolor: 'background.paper',
                              borderRight: `1px solid ${COLORS.border}`,
                            }),
                          }}
                          title={text}
                        >
                          <Box component="span" className="app-table-cell-text" sx={{ maxWidth: 'none' }}>
                            {text}
                          </Box>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              )}

              {filteredRows.length > 0 ? (
                <TableRow>
                  {visibleColumns.map((col, idx) => {
                    const numeric = isNumericKind(col.kind)
                    const stickyLeft = col.sticky ? stickyOffsets[col.key] : undefined
                    const showLabel = idx === 0
                    const text = col.totalAgg && col.totalAgg !== 'none'
                      ? formatCell(total[col.key], col.kind)
                      : showLabel ? 'Total' : ''
                    return (
                      <TableCell
                        key={col.key}
                        align={numeric ? 'right' : 'left'}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          bgcolor: COLORS.bgSubtle,
                          borderTop: `1px solid ${COLORS.borderStrong}`,
                          borderBottom: 'none',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 3,
                          ...(stickyLeft != null && {
                            left: stickyLeft,
                            zIndex: 4,
                            borderRight: `1px solid ${COLORS.border}`,
                          }),
                        }}
                      >
                        {text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Global Search popover */}
      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
            placeholder={`Search ${title.toLowerCase()} rows…`}
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      {/* Per-column filter popover — duy nhất 1 instance per section. */}
      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
        }}
        options={popoverOptions}
      />
    </Stack>
  )
}

/* ---------- tab entry ----------------------------------------------------- */

export default function BatchReportTab({ inputRows, outputRows, showAdvanced }: Props) {
  return (
    <Stack spacing={2}>
      <BatchSection
        title="INPUT"
        columns={INPUT_COLUMNS}
        rows={inputRows}
        showAdvanced={showAdvanced}
        storageKeyPrefix="batch-input"
      />
      <BatchSection
        title="OUTPUT"
        columns={OUTPUT_COLUMNS}
        rows={outputRows}
        showAdvanced={showAdvanced}
        storageKeyPrefix="batch-output"
      />
    </Stack>
  )
}
